import React, { useState, useEffect } from "react";
import { useAlert } from "react-alert";
import {
  Table, TableHead, TableBody, TableRow, TableCell, Button, IconButton, Tooltip,
  Chip, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Select,
  MenuItem, FormControl, InputLabel, Box, Typography, Alert, CircularProgress
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import SendIcon from "@mui/icons-material/Send";
import DownloadIcon from "@mui/icons-material/Download";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import RefreshIcon from "@mui/icons-material/Refresh";
import Menu from "../../Menu";
import Header from "../../Header";
import { FormsApi, CampaignsApi, downloadCampaignTemplate, fmtDateTime } from "../formsUtils";
import { errorBody, errorMessage } from "../../../services/http";

const STATUS = {
  draft: { label: "Rascunho", color: "default" },
  sending: { label: "Enviando", color: "info" },
  sent: { label: "Enviada", color: "success" },
  partial: { label: "Parcial", color: "warning" },
  failed: { label: "Falhou", color: "error" },
};

const emptyForm = { name: "", form_id: "", template_id: "", file: null };

export default function Campaigns() {
  const alert = useAlert();
  const [rows, setRows] = useState([]);
  const [forms, setForms] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [dispatching, setDispatching] = useState(null);
  const [result, setResult] = useState(null);

  function load() {
    CampaignsApi.list("?page=1&limit=100").then(
      (resp) => setRows(resp?.data?.data || []),
      (error) => console.log(error)
    );
  }

  useEffect(() => {
    load();
    FormsApi.list("?page=0&limit=200").then(
      (resp) => setForms((resp?.data?.data || []).filter((f) => f.status === "published")),
      () => {}
    );
    FormsApi.listTemplates().then(
      (resp) => setTemplates((resp?.data?.data || []).filter((t) => t.active)),
      () => {}
    );
  }, []);

  function openNew() {
    setForm(emptyForm);
    setResult(null);
    setOpen(true);
  }

  function handleCreate() {
    if (!form.name.trim()) { alert.error("Informe o nome da campanha"); return; }
    if (!form.form_id) { alert.error("Selecione o formulário"); return; }
    if (!form.file) { alert.error("Selecione o arquivo CSV/Excel"); return; }

    const fd = new FormData();
    fd.append("name", form.name);
    fd.append("form_id", form.form_id);
    if (form.template_id) fd.append("template_id", form.template_id);
    fd.append("upload", form.file);

    setSaving(true);
    CampaignsApi.create(fd).then(
      (resp) => {
        setSaving(false);
        setResult(resp.data);
        load();
        alert.success(`Campanha criada: ${resp.data.imported} contato(s) importado(s)`);
      },
      (error) => {
        setSaving(false);
        const d = errorBody(error);
        setResult(d && d.invalid ? d : null);
        alert.error(d?.message || "Erro ao criar campanha", { timeout: 15000 });
      }
    );
  }

  function handleDispatch(row) {
    if (!row.template_id) {
      alert.error("Esta campanha não tem template de WhatsApp. Crie com um template selecionado.");
      return;
    }
    if (!window.confirm(`Disparar a campanha "${row.name}" para os contatos pendentes?`)) return;
    setDispatching(row.id);
    CampaignsApi.dispatch(row.id).then(
      (resp) => {
        setDispatching(null);
        alert.success(`Disparo: ${resp.data.sent} enviado(s), ${resp.data.failed} falha(s)`);
        load();
      },
      (error) => {
        setDispatching(null);
        alert.error(errorMessage(error, "Erro ao disparar"), { timeout: 15000 });
      }
    );
  }

  return (
    <div id="wrapper">
      <Menu />
      <div id="page-wrapper" className="gray-bg">
        <Header />
        <div className="row wrapper border-bottom white-bg page-heading">
          <div className="col-lg-8">
            <h2>Campanhas de disparo</h2>
            <small>Envie um formulário para uma lista de contatos (CSV/Excel) via WhatsApp</small>
          </div>
          <div className="col-lg-4" style={{ textAlign: "right", paddingTop: 20 }}>
            <Button startIcon={<RefreshIcon />} onClick={load} sx={{ mr: 1 }}>Atualizar</Button>
            <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={openNew}>
              Nova campanha
            </Button>
          </div>
        </div>

        <div className="wrapper wrapper-content animated fadeInRight">
          <div className="row"><div className="col-lg-12"><div className="ibox"><div className="ibox-content">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Campanha</TableCell>
                  <TableCell>Formulário</TableCell>
                  <TableCell>Template</TableCell>
                  <TableCell align="center">Contatos</TableCell>
                  <TableCell align="center">Enviados</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Criada</TableCell>
                  <TableCell align="right">Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => {
                  const st = STATUS[row.status] || STATUS.draft;
                  return (
                    <TableRow key={row.id} hover>
                      <TableCell><strong>{row.name}</strong></TableCell>
                      <TableCell>{row.form_title}</TableCell>
                      <TableCell>{row.template_name || <span style={{ color: "#c62828" }}>— sem template —</span>}</TableCell>
                      <TableCell align="center">{row.total}</TableCell>
                      <TableCell align="center">{row.sent}</TableCell>
                      <TableCell><Chip size="small" label={st.label} color={st.color} /></TableCell>
                      <TableCell>{fmtDateTime(row.created_at)}</TableCell>
                      <TableCell align="right">
                        <Tooltip title="Disparar pendentes">
                          <span>
                            <IconButton size="small" color="primary" disabled={dispatching === row.id}
                              onClick={() => handleDispatch(row)}>
                              {dispatching === row.id ? <CircularProgress size={18} /> : <SendIcon fontSize="small" />}
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {rows.length === 0 && (
                  <TableRow><TableCell colSpan={8} align="center" style={{ color: "#999", padding: 40 }}>
                    Nenhuma campanha ainda. Clique em "Nova campanha".
                  </TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </div></div></div></div>
        </div>
      </div>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Nova campanha</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            <TextField label="Nome da campanha" value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })} fullWidth />

            <FormControl fullWidth>
              <InputLabel>Formulário (publicado)</InputLabel>
              <Select label="Formulário (publicado)" value={form.form_id}
                onChange={(e) => setForm({ ...form, form_id: e.target.value })}>
                {forms.map((f) => <MenuItem key={f.id} value={f.id}>{f.title}</MenuItem>)}
              </Select>
              {forms.length === 0 && <Typography variant="caption" color="error">Publique um formulário primeiro.</Typography>}
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Template de WhatsApp</InputLabel>
              <Select label="Template de WhatsApp" value={form.template_id}
                onChange={(e) => setForm({ ...form, template_id: e.target.value })}>
                <MenuItem value=""><em>(definir depois)</em></MenuItem>
                {templates.map((t) => <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>)}
              </Select>
            </FormControl>

            <Box>
              <Button variant="outlined" component="label" startIcon={<UploadFileIcon />}>
                {form.file ? form.file.name : "Selecionar CSV/Excel"}
                <input hidden type="file" accept=".csv,.xlsx,.xls"
                  onChange={(e) => setForm({ ...form, file: e.target.files[0] || null })} />
              </Button>
              <Box sx={{ mt: 1 }}>
                <Button size="small" startIcon={<DownloadIcon />} onClick={downloadCampaignTemplate}>
                  Baixar modelo (nome, telefone, cpf)
                </Button>
              </Box>
            </Box>

            {result && (
              <Alert severity={result.imported ? "success" : "warning"}>
                {result.imported ? `${result.imported} contato(s) importado(s). ` : ""}
                {result.invalid && result.invalid.length > 0 && (
                  <span>{result.invalid.length} linha(s) ignorada(s): {result.invalid.slice(0, 5).map((i) => `linha ${i.line}`).join(", ")}{result.invalid.length > 5 ? "..." : ""}</span>
                )}
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Fechar</Button>
          <Button variant="contained" onClick={handleCreate} disabled={saving}>
            {saving ? "Importando..." : "Criar campanha"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
